import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import * as intlTelInput from 'intl-tel-input';
import { CountryData, IntlTelInputOptions } from './model/atlas-inputphone.options';
import { UserService } from '../../../services/user.service';
import { Subscription } from 'rxjs';
import { getNation3To2 } from '../../../utils/utils';

/*
*                       [options]="{
preferredCountries: ['ch'],
localizedCountries: { ch: 'Suisse' },
onlyCountries: ['fr', 'ch']
}"
 [onlyLocalized]="true"
 */

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AtlasInputPhoneComponent),
  multi: true
};

const noop = () => {};

/** @title Basic datepicker */
@Component({
  selector: 'wsb-atlas-inputphone',
  templateUrl: 'atlas-inputphone.component.html',
  styleUrls: ['atlas-inputphone.component.scss'],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
  standalone: false
})
export class AtlasInputPhoneComponent implements ControlValueAccessor, AfterViewInit, OnInit, OnDestroy {
  @Input() public cssClass!: string;
  @Input() public E164PhoneNumber!: string | null;
  @Input() public label!: string;
  @Input() public labelCssClass!: string;
  @Input() public name = 'intl-tel-input-name';
  @Input() public onlyLocalized!: boolean;
  @Input() public options: IntlTelInputOptions = {};
  @Input() public required!: boolean;
  @Input() public readonly = false;
  @Input() public checkUserPreferences = true;
  @Input() public mainNation = null;
  @Output() private E164PhoneNumberChange = new EventEmitter<string | null>();
  @Output() phoneNumber: UntypedFormControl = new UntypedFormControl('', [Validators.pattern(/^(\+)?[0-9]{5,15}$/)]);

  innerValue: string;

  @ViewChild('intlTelInput') private pinputElement!: ElementRef;
  pphoneNumber!: string;
  private pintlTelInput: any;
  private outValue: string;
  private userSubscription: Subscription;

  // @ts-ignore
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;
  loaded = false;

  private defaultNation = 'gb';
  private defaultPreferred = ['gb', 'it', 'fr', 'es', 'ch'];

  private static modifyCountryData(): void {
    (window as any).intlTelInputGlobals
      .getCountryData()
      .forEach((country: CountryData) => (country.name = country.name.replace(/.+\((.+)\)/, '$1')));
  }

  // get phoneNumber(): string {
  //   return this.pphoneNumber;
  // }

  // set phoneNumber(value: string) {
  //   // if (!!value) {
  //   //   this.pintlTelInput.setNumber(value);
  //   // }
  //   this.pphoneNumber = value;
  //   this.value = value;
  //   this.onBlur();
  // }

  constructor(private userService: UserService) {}

  setDefaultByNation(nation?) {
    let defaultNation = this.defaultNation;
    let defaultPreferred = this.defaultPreferred;
    if (nation) {
      const twoCountry = getNation3To2(nation);
      if (twoCountry) {
        if ((window as any).intlTelInputGlobals.getCountryData().find((country: CountryData) => country.iso2 === defaultNation)) {
          defaultNation = twoCountry;
          defaultPreferred = defaultPreferred.filter(item => item !== defaultNation);
          defaultPreferred.unshift(defaultNation);
        }
      }
    }
    this.options.initialCountry = defaultNation;
    this.options.preferredCountries = defaultPreferred;
  }

  ngOnInit() {
    this.options.formatOnDisplay = false;

    this.phoneNumber.valueChanges.subscribe(value => {
      this.pphoneNumber = value;
      this.value = value;
      this.onBlur();
    });
    if (this.checkUserPreferences) {
      this.userSubscription = this.userService.getAuthenticationState().subscribe(loggedUser => {
        this.setDefaultByNation(loggedUser ? loggedUser.defaultNation : null);
        this.loaded = true;
      });
    } else {
      this.setDefaultByNation(this.mainNation ? this.mainNation : null);
      this.loaded = true;
    }
  }

  ngOnDestroy(): void {
    if (this.checkUserPreferences) {
      this.userSubscription.unsubscribe();
    }
  }

  get intlTelInput(): any {
    return this.pintlTelInput;
  }

  public ngAfterViewInit(): void {
    if (this.onlyLocalized) {
      AtlasInputPhoneComponent.modifyCountryData();
    }

    const intlTelInputInstance = intlTelInput;
    this.pintlTelInput = intlTelInputInstance(this.pinputElement.nativeElement, this.options);
    this.listenForCountryChange();
    this.removeWhiteSpaces();

    if (this.innerValue) {
      this.pintlTelInput.promise.then(() => {
        this.pintlTelInput.setNumber(this.innerValue);
        this.i18nizePhoneNumber();
      });
    }
  }

  public i18nizePhoneNumber(setValue?): void {
    if (setValue) {
      if (this.innerValue && this.innerValue !== this.pintlTelInput.getNumber()) {
        this.phoneNumber.setValue(this.pintlTelInput.getNumber());
      }
    }
    this.E164PhoneNumber = null;
    if (this.pintlTelInput.isValidNumber()) {
      this.E164PhoneNumber = this.pintlTelInput.getNumber();
    }
    this.E164PhoneNumberChange.emit(this.E164PhoneNumber);
  }

  // get accessor
  get value(): string {
    this.outValue = this.pintlTelInput.getNumber();
    return this.outValue;
  }

  // set accessor including call the onchange callback
  set value(v: string) {
    if (v !== this.innerValue && v !== this.outValue) {
      this.innerValue = v;
    }
  }

  // // get accessor
  // get valueIn(): string {
  //   return this.innerValue;
  // }
  //
  // // set accessor including call the onchange callback
  // set valueIn(v: string) {
  //   if (v !== this.innerValue) {
  //     this.innerValue = v;
  //   }
  //   this.onChangeCallback(this.value);
  // }

  // Occured value changed from module
  writeValue(value: any): void {
    if (value !== this.innerValue) {
      try {
        this.innerValue = value;
        if (this.pintlTelInput && this.pintlTelInput.setNumber) {
          this.pintlTelInput.setNumber(value);
          this.i18nizePhoneNumber();
        }
      } catch (e) {
        this.innerValue = '';
      }
      // else {
      //   this.pintlTelInput.promise.then(() => {
      //     this.pintlTelInput.setNumber(value);
      //   });
      // }
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  onChange(event) {
    this.value = event;
    this.onBlur();
  }

  onBlur() {
    this.onChangeCallback(this.value);
  }

  listenForCountryChange() {
    this.pinputElement.nativeElement.addEventListener('countrychange', e => {
      let placeholder = e.currentTarget.getAttribute('placeholder');
      placeholder = placeholder.replace(/\s/g, '');
      e.currentTarget.setAttribute('placeholder', placeholder);
    });
  }

  removeWhiteSpaces() {
    let placeholder = this.pinputElement.nativeElement.getAttribute('placeholder');
    placeholder = placeholder.replace(/\s/g, '');
    this.pinputElement.nativeElement.setAttribute('placeholder', placeholder);
  }
}
